import { FormProvider, useForm } from 'react-hook-form';
import { makeStyles } from 'tss-react/mui';

import { yupResolver } from '@hookform/resolvers/yup';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Theme,
  Typography,
} from '@mui/material';

import { UPLOAD_FILE_TYPE_OPTIONS } from '../../constants/constants';
import FormSubmitButton from '../../components/buttons/FormSubmitButton';
import FormSelectField from '../../components/formFields/FormSelectField';
import FormCancelButton from '../../components/buttons/FormCancelButton';
import FormTextField from '../../components/formFields/FormTextField';
import FormCheckboxField from '../../components/formFields/FormCheckboxField';
import FormImageField from '../../components/formFields/FormImageField';
import { DATA_FILE_UPLOAD_FORM_DEFAULT_VALUES } from '../../helpers/formSchemas/formDefaultValues';
import { dataFileUploadFormSchema } from '../../helpers/formSchemas/formSchemas';

const useStyles = makeStyles()((theme: Theme) => ({
  popupContainer: {
    '& .MuiDialog-paper': {
      backgroundColor: theme.palette.primary.light,
      color: theme.palette.common.white,
      padding: '32px',
      maxWidth: '670px',
      width: '100%',
      boxSizing: 'border-box',
      borderRadius: '16px',
    },

    '& .MuiDialogContentText-root': {
      color: theme.palette.common.white,
    },
  },
  popupTitle: {
    fontSize: '28px',
    lineHeight: '120%',
    fontFamily: 'Poppins',
    fontWeight: 600,
    fontStyle: 'normal',
    padding: 0,
    marginBottom: '8px',
  },
  popupContentContainer: {
    padding: 0,
    paddingTop: '10px',
  },
  popupSecondaryText: {
    fontSize: '16px',
    lineHeight: '120%',
    fontFamily: 'Inter',
    fontWeight: 500,
    fontStyle: 'normal',
    color: '#C3C4C8',
    marginBottom: '22px',
  },
  deleteIcon: {
    padding: 0,
    svg: {
      color: theme.palette.error.main,
      marginTop: '1px',
    },
  },
  popupActions: {
    display: 'flex',
    flexDirection: 'column',
    maxWidth: '416px',
    width: '100%',
    margin: '0 auto',
    gap: '16px',
    padding: 0,
    marginTop: '72px',
  },
  checkboxLabel: {
    fontFamily: 'Inter',
    fontSize: '16px',
    fontWeight: 500,
    lineHeight: '120%',
    color: theme.palette.common.white,
  },
  checkboxWrapper: {
    display: 'flex',
    alignItems: 'center',

    '& label': {
      marginRight: 0,
    },
  },
}));

interface DataFileUploadPopupProps {
  isOpen: boolean;
  onClose: () => void;
}

const DataFileUploadPopup = ({ isOpen, onClose }: DataFileUploadPopupProps) => {
  const { classes } = useStyles();

  const formMethods = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: yupResolver(dataFileUploadFormSchema),
    defaultValues: DATA_FILE_UPLOAD_FORM_DEFAULT_VALUES,
  });

  const {
    reset,
    watch,
    setValue,
    formState: { isValid },
  } = formMethods;

  const fileTypeFieldValue = watch('fileType');

  const handleSetFileName = (newValue: string) => {
    setValue('fileName', newValue, { shouldDirty: true });
  };

  const handleClosePopup = () => {
    reset();
    onClose();
  };

  return (
    <Dialog open={isOpen} className={classes.popupContainer} onClose={handleClosePopup}>
      <Box display="flex" justifyContent="flex-end" height="24px">
        <IconButton
          disableRipple
          onClick={handleClosePopup}
          sx={{
            color: 'white',
            width: '24px',
            height: '24px',
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <DialogTitle className={classes.popupTitle}>Upload a File for Processing</DialogTitle>
      <DialogContent className={classes.popupContentContainer}>
        <FormProvider {...formMethods}>
          <Box display="flex" padding="40px 32px 32px 32px" gap="32px">
            <FormSelectField
              name="fileType"
              label="File Type"
              options={UPLOAD_FILE_TYPE_OPTIONS}
              width="100%"
              maxWidth="182px"
            />
            <Box display="flex" gap="10px" width="100%">
              <FormTextField name="fileName" label="File Name" minWidth="230px" readOnly />
              <FormImageField
                name="fileData"
                label="Edit"
                disabled={!fileTypeFieldValue}
                customHandler={handleSetFileName}
              />
            </Box>
          </Box>
          {fileTypeFieldValue === 'Clients' && (
            <Box display="flex" paddingLeft="32px" gap="12px" alignItems="center">
              <Box className={classes.checkboxWrapper}>
                <FormCheckboxField name="sendInvitationVia" />
                <Typography className={classes.checkboxLabel}>Send invitation via</Typography>
              </Box>
              <FormSelectField
                name="sendVia"
                label="Channel"
                options={[
                  { id: 'SMS', label: 'SMS' },
                  { id: 'Email', label: 'Email' },
                ]}
                width="100%"
                maxWidth="120px"
              />
              <Typography className={classes.checkboxLabel}>for</Typography>
              <FormSelectField
                name="access"
                label="Invitational Options"
                options={[
                  { id: 'appOnly', label: 'App Access Only' },
                  { id: 'appAndVirtualCard', label: 'App + Virtual Card' },
                  { id: 'appAndPhysicalCard', label: 'App + Physical Card' },
                ]}
                width="100%"
                maxWidth="200px"
              />
            </Box>
          )}
        </FormProvider>
      </DialogContent>
      <DialogActions className={classes.popupActions}>
        <FormSubmitButton disabled={!isValid} buttonText="Submit" onClick={handleClosePopup} />

        <FormCancelButton onClick={handleClosePopup} />
      </DialogActions>
    </Dialog>
  );
};

export default DataFileUploadPopup;
